// services/pollService.js
//
// Voting on an invitation's polls, from the browser.
//
// The polls themselves live in `events.polls` (a JSONB array the create form
// writes); the answers live in `event_poll_responses`, one row per person per
// poll. This is the app's src/features/events/api/pollService.js, narrowed to
// what a guest on the web needs: cast a vote, and see the result.
//
// ── The shape matters ──────────────────────────────────────────────────────
//
// `selected_options` holds option INDICES, not labels — the app counts votes
// by indexing into `poll.options`, so storing text here would make every vote
// cast on the web uncountable in the app. It is always an array, even for a
// single choice, because the app's reader accepts both and writing one shape
// is better than relying on that.
//
// The upsert conflict target is the app's, so voting again replaces rather
// than stacking up rows.

import supabase from './supabase';

const CONFLICT = 'event_id,financial_year,poll_id,user_id';

/**
 * Cast or change a vote.
 *
 * @param {string} eventId
 * @param {string|number} financialYear
 * @param {string} pollId
 * @param {number[]} selectedOptions indices into poll.options
 */
export async function submitPollResponse(eventId, financialYear, pollId, selectedOptions) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in to vote.');

  const { data, error } = await supabase
    .from('event_poll_responses')
    .upsert(
      {
        event_id: eventId,
        financial_year: financialYear,
        poll_id: pollId,
        user_id: user.id,
        selected_options: selectedOptions,
        updated_at: new Date().toISOString(),
      },
      { onConflict: CONFLICT, ignoreDuplicates: false },
    )
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Every answer to every poll on this invitation, in one request.
 *
 * One query rather than one per poll: an invitation can carry several, and a
 * request each would be a waterfall on the slowest part of the page.
 *
 * @returns {Promise<{byPoll: Record<string, {counts: number[], total: number}>, mine: Record<string, number[]>}>}
 */
export async function getPollResults(eventId, financialYear, polls, userId) {
  const empty = { byPoll: {}, mine: {} };
  if (!eventId || !Array.isArray(polls) || polls.length === 0) return empty;

  const { data, error } = await supabase
    .from('event_poll_responses')
    .select('poll_id, user_id, selected_options')
    .eq('event_id', eventId)
    .eq('financial_year', financialYear);

  if (error) {
    // A poll that cannot show its tally is still a poll worth showing, so
    // this reports nothing rather than taking the section down.
    console.warn('Could not load poll results:', error.message);
    return empty;
  }

  const byPoll = {};
  const mine = {};

  for (const poll of polls) {
    byPoll[poll.id] = { counts: new Array(poll.options?.length ?? 0).fill(0), total: 0 };
  }

  for (const row of data ?? []) {
    const tally = byPoll[row.poll_id];
    if (!tally) continue; // an answer to a poll the host has since deleted

    // Older rows may hold a bare number rather than an array.
    const chosen = Array.isArray(row.selected_options)
      ? row.selected_options
      : [row.selected_options];

    let counted = false;
    for (const raw of chosen) {
      const index = typeof raw === 'number' ? raw : parseInt(raw, 10);
      if (Number.isInteger(index) && index >= 0 && index < tally.counts.length) {
        tally.counts[index] += 1;
        counted = true;
      }
    }
    // `total` is people, not ticks: a multi-choice answer is still one voter.
    if (counted) tally.total += 1;

    if (userId && row.user_id === userId) {
      mine[row.poll_id] = chosen
        .map((raw) => (typeof raw === 'number' ? raw : parseInt(raw, 10)))
        .filter((n) => Number.isInteger(n));
    }
  }

  return { byPoll, mine };
}
